import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Search, Command, Circle, Clock, Trophy } from 'lucide-react';
import { useSearchExplorer } from '../../contexts/SearchExplorerContext';
import proService, { ProEvent } from '../../services/proService';
import PlayerAvatar from '../spiele/PlayerAvatar';

const ACCENT = '#3fced1';
const ROTATING_WORDS = ['Turniere', 'Vereine', 'Spielstätten', 'Dart-Shops'];

/**
 * Hero-Sektion der Startseite – Port des Flutter SearchHeader.
 * Branding + animierte Headline + Such-Trigger (öffnet SearchExplorer),
 * daneben eine "Spiele heute"-Karte (Pro Events).
 */
const HeroSection: React.FC = () => {
  const { open: openSearch } = useSearchExplorer();
  const [wordIndex, setWordIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setWordIndex((prev) => (prev + 1) % ROTATING_WORDS.length);
    }, 2200);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="relative overflow-hidden border-b border-zinc-900">
      {/* Glow-Hintergrund */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(60% 80% at 20% 10%, rgba(63,206,209,0.15), transparent 60%), radial-gradient(50% 60% at 90% 20%, rgba(63,206,209,0.08), transparent 60%)',
        }}
      />
      {/* Animierte Glow-Blobs */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-24 -left-24 h-72 w-72 animate-dc-blob rounded-full bg-[#3fced1]/15 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute top-1/3 right-[-6rem] h-80 w-80 animate-dc-blob-slow rounded-full bg-[#3fced1]/10 blur-3xl"
      />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-10 px-4 py-14 sm:px-6 lg:grid-cols-[1.2fr_0.8fr] lg:py-24">
        {/* Linke Spalte: Branding + Suche */}
        <div className="flex flex-col justify-center">
          <div className="mb-6 flex items-center gap-3">
            <img
              src="/icon.png"
              alt="DartCloud"
              className="h-11 w-11 rounded-xl object-cover"
            />
            <span className="text-xl font-bold text-white">DartCloud</span>
            <span className="rounded-full border border-[#3fced1]/30 bg-[#3fced1]/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-[#3fced1]">
              Suchen. Finden. Spielen.
            </span>
          </div>

          <h1 className="text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-6xl">
            Finde jetzt
            <br />
            <span className="accent-gradient-text">{ROTATING_WORDS[wordIndex]}</span>
            <br />
            in deiner Nähe
          </h1>

          <p className="mt-6 max-w-md text-base text-gray-400 sm:text-lg">
            DartCloud bietet dir eine präzise Suche nach allem rund um den Dartsport.
          </p>

          {/* Such-Trigger */}
          <button
            type="button"
            onClick={openSearch}
            className="mt-8 flex h-14 w-full max-w-md items-center gap-3 rounded-2xl border border-zinc-700 bg-zinc-800/70 px-4 text-left shadow-lg shadow-black/20 transition-all hover:-translate-y-0.5 hover:border-[#3fced1]/60 hover:bg-zinc-800 hover:shadow-[0_12px_30px_-12px_rgba(63,206,209,0.35)]"
          >
            <Search size={22} style={{ color: ACCENT }} />
            <span className="flex-1 text-[15px] text-gray-500">Suchen...</span>
            <span
              className="flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-black"
              style={{ backgroundColor: ACCENT }}
            >
              <Command size={13} /> K
            </span>
          </button>

          {/* Sekundäre CTAs */}
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <RouterLink
              to="/spiele"
              className="inline-flex items-center gap-1.5 rounded-full border border-zinc-700 bg-zinc-900/60 px-4 py-2 text-sm font-semibold text-gray-300 transition-colors hover:border-[#3fced1]/60 hover:text-white"
            >
              <Circle size={9} className="animate-pulse fill-red-500 text-red-500" />
              Spiele & Live-Scores
            </RouterLink>
            <RouterLink
              to="/turniere"
              className="inline-flex items-center gap-1.5 rounded-full border border-zinc-700 bg-zinc-900/60 px-4 py-2 text-sm font-semibold text-gray-300 transition-colors hover:border-[#3fced1]/60 hover:text-white"
            >
              <Trophy size={14} style={{ color: ACCENT }} />
              Turniere entdecken
            </RouterLink>
          </div>
        </div>

        {/* Rechte Spalte: Spiele heute */}
        <SpieleHeuteCard />
      </div>
    </section>
  );
};

// ---------------------------------------------------------------------------
// Spiele heute – Pro Events des heutigen Tages
// ---------------------------------------------------------------------------

const SpieleHeuteCard: React.FC = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState<ProEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    proService
      .getEventsByDateWithStats(0)
      .then((list) => {
        if (!cancelled) setEvents(Array.isArray(list) ? list.slice(0, 3) : []);
      })
      .catch(() => {
        if (!cancelled) setEvents([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="flex flex-col overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900/60 shadow-xl shadow-black/20 backdrop-blur-sm">
      <div className="px-5 py-4">
        <h2 className="section-heading text-lg font-bold text-white">Spiele heute</h2>
      </div>
      <div className="h-px bg-gradient-to-r from-[#3fced1]/40 via-zinc-800 to-transparent" />

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div
            className="h-6 w-6 animate-spin rounded-full border-2 border-zinc-700"
            style={{ borderTopColor: ACCENT }}
          />
        </div>
      ) : events.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
          <Trophy size={40} className="text-zinc-700" />
          <span className="text-sm text-gray-500">Keine Spiele heute</span>
        </div>
      ) : (
        <div>
          {events.map((event, idx) => (
            <SpielItem key={event.id || event._id || idx} event={event} />
          ))}
          <button
            type="button"
            onClick={() => navigate('/spiele')}
            className="w-full px-5 py-3.5 text-sm font-semibold transition-colors hover:bg-zinc-800/60"
            style={{ color: ACCENT }}
          >
            Alle anzeigen
          </button>
        </div>
      )}
    </div>
  );
};

const SpielItem: React.FC<{ event: ProEvent }> = ({ event }) => {
  const isLive = proService.isLive(event);
  const scheduled = proService.isScheduled(event);
  const scores = proService.formatScore(event);
  const homeWon = event.winner === 1;
  const awayWon = event.winner === 2;
  const homeName = proService.getParticipantName(event.homeParticipant);
  const awayName = proService.getParticipantName(event.awayParticipant);

  const nameClass = (won: boolean) =>
    `flex-1 truncate text-sm ${won ? 'font-bold' : 'font-medium text-white'}`;
  const nameStyle = (won: boolean) => (won ? { color: ACCENT } : undefined);

  return (
    <div className="border-b border-zinc-800 px-5 py-3.5 last:border-b-0">
      {event.tournament?.name && (
        <p className="mb-2 truncate text-xs font-medium text-gray-500">
          {event.tournament.name}
        </p>
      )}

      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <PlayerAvatar participant={event.homeParticipant} />
          <span className={nameClass(homeWon)} style={nameStyle(homeWon)}>
            {homeName}
          </span>
        </div>
        {!scheduled && (
          <span
            className={`rounded-md px-2 py-0.5 text-sm font-bold tabular-nums ${
              homeWon ? '' : 'bg-zinc-800 text-zinc-400'
            }`}
            style={homeWon ? { backgroundColor: 'rgba(63,206,209,0.1)', color: ACCENT } : undefined}
          >
            {scores.home}
          </span>
        )}
      </div>

      <div className="mt-1.5 flex items-center justify-between gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <PlayerAvatar participant={event.awayParticipant} />
          <span className={nameClass(awayWon)} style={nameStyle(awayWon)}>
            {awayName}
          </span>
        </div>
        {!scheduled && (
          <span
            className={`rounded-md px-2 py-0.5 text-sm font-bold tabular-nums ${
              awayWon ? '' : 'bg-zinc-800 text-zinc-400'
            }`}
            style={awayWon ? { backgroundColor: 'rgba(63,206,209,0.1)', color: ACCENT } : undefined}
          >
            {scores.away}
          </span>
        )}
      </div>

      <div className="mt-2 flex items-center gap-1.5">
        {isLive ? (
          <>
            <Circle size={10} className="animate-pulse fill-red-500 text-red-500" />
            <span className="text-xs font-semibold text-red-500">Live</span>
          </>
        ) : (
          <>
            <Clock size={12} className="text-gray-500" />
            <span className="text-xs text-gray-500">
              {proService.formatEventTime(event.startTime)}
            </span>
          </>
        )}
      </div>
    </div>
  );
};

export default HeroSection;
